import { FirebaseError } from 'firebase/app';

import type { LimitExceededDetails } from '@superfunnel/types/usage-limits';

const FREE_EMAIL_DOMAIN_BLOCKED_TOKEN = 'FREE_EMAIL_DOMAIN_BLOCKED';
const FREE_EMAIL_DOMAIN_BLOCKED_MESSAGE =
  'Use your work email address to create a SuperFunnel account.';

interface ZodIssue {
  path: (string | number)[];
  message: string;
  code?: string;
}

interface FirebaseAuthServerResponse {
  message?: unknown;
  error?: {
    message?: unknown;
  };
}

/**
 * Parses a Firebase/Zod error and returns a user-friendly message
 */
export function parseFirebaseError(error: unknown): string {
  // Handle FirebaseError with Zod validation details
  if (error instanceof FirebaseError) {
    const authBlockingMessage = parseAuthBlockingError(error);
    if (authBlockingMessage) {
      return authBlockingMessage;
    }

    const storageMessage = parseStorageError(error);
    if (storageMessage) {
      return storageMessage;
    }

    // Check if it's a validation error with Zod issues in details
    const details = (error as FirebaseError & { details?: unknown }).details;

    if (Array.isArray(details) && details.length > 0) {
      return formatZodIssues(details as ZodIssue[]);
    }

    // Return the Firebase error message
    return error.message || 'An error occurred';
  }

  // Handle standard Error
  if (error instanceof Error) {
    return error.message;
  }

  // Fallback for unknown error types
  return 'An unexpected error occurred';
}

/**
 * Maps Firebase Storage errors (e.g. security-rule rejections) to friendly
 * messages. Rule rejections surface as `storage/unauthorized`, which usually
 * means the file's type or size isn't allowed.
 */
function parseStorageError(error: FirebaseError): string | null {
  switch (error.code) {
    case 'storage/unauthorized':
      return "This file can't be uploaded. It may be an unsupported file type or exceed the size limit.";
    case 'storage/quota-exceeded':
      return 'Upload failed because the storage quota has been exceeded.';
    case 'storage/retry-limit-exceeded':
      return 'Upload timed out. Check your connection and try again.';
    case 'storage/canceled':
      return 'Upload canceled.';
    default:
      return null;
  }
}

/**
 * Formats Zod validation issues into a readable message
 */
export function formatZodIssues(issues: ZodIssue[]): string {
  if (issues.length === 0) {
    return 'Validation failed';
  }

  if (issues.length === 1) {
    const issue = issues[0];
    const path = formatPath(issue.path);
    return path ? `${path}: ${issue.message}` : issue.message;
  }

  // Multiple issues - list them
  const formattedIssues = issues.slice(0, 3).map((issue) => {
    const path = formatPath(issue.path);
    return path ? `${path}: ${issue.message}` : issue.message;
  });

  const remaining = issues.length - 3;
  if (remaining > 0) {
    formattedIssues.push(
      `...and ${remaining} more issue${remaining > 1 ? 's' : ''}`,
    );
  }

  return formattedIssues.join('; ');
}

/**
 * Formats a Zod path array into a readable string
 */
function formatPath(path: (string | number)[]): string {
  if (!path || path.length === 0) {
    return '';
  }

  return path
    .map((segment, index) => {
      if (typeof segment === 'number') {
        return `[${segment}]`;
      }
      // Convert camelCase to Title Case
      const formatted = segment
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
      return index === 0 ? formatted : formatted.toLowerCase();
    })
    .join(' → ');
}

function parseAuthBlockingError(error: FirebaseError): string | null {
  if (error.code !== 'auth/internal-error') {
    return null;
  }

  const customData = (
    error as FirebaseError & {
      customData?: {
        _serverResponse?: FirebaseAuthServerResponse | string;
      };
    }
  ).customData;

  const taggedMessage = [
    getFirebaseAuthServerMessage(customData?._serverResponse),
    error.message,
  ]
    .map(extractTaggedBlockingMessage)
    .find((message): message is string => !!message);

  return taggedMessage ?? null;
}

function getFirebaseAuthServerMessage(
  serverResponse?: FirebaseAuthServerResponse | string,
): string | null {
  if (typeof serverResponse === 'string') {
    return serverResponse;
  }

  if (!serverResponse) {
    return null;
  }

  const message = serverResponse.error?.message ?? serverResponse.message;

  return typeof message === 'string' ? message : null;
}

function extractTaggedBlockingMessage(message: string | null): string | null {
  if (!message || !message.includes(FREE_EMAIL_DOMAIN_BLOCKED_TOKEN)) {
    return null;
  }

  const tokenIndex = message.indexOf(FREE_EMAIL_DOMAIN_BLOCKED_TOKEN);
  const taggedMessage = message.slice(
    tokenIndex + FREE_EMAIL_DOMAIN_BLOCKED_TOKEN.length,
  );
  const cleanedMessage = taggedMessage
    .replace(/^[\]\s:-]+/, '')
    .replace(/["']?,\s*"status".*$/i, '')
    .replace(/\s*\(auth\/[\w-]+\).*$/i, '')
    .replace(/["')\]}\s]+$/, '')
    .trim();

  return cleanedMessage || FREE_EMAIL_DOMAIN_BLOCKED_MESSAGE;
}

/**
 * Checks if an error is a limit-exceeded error from the backend.
 */
export function isLimitError(error: unknown): boolean {
  if (!(error instanceof FirebaseError)) return false;
  if (error.code !== 'functions/resource-exhausted') return false;
  const details = (error as FirebaseError & { details?: unknown }).details;
  return (
    typeof details === 'object' &&
    details !== null &&
    (details as Record<string, unknown>).code === 'LIMIT_EXCEEDED'
  );
}

/**
 * Extracts LimitExceededDetails from a limit error.
 * Returns null if the error is not a limit error.
 */
export function parseLimitError(error: unknown): LimitExceededDetails | null {
  if (!isLimitError(error)) return null;
  return (error as FirebaseError & { details: LimitExceededDetails }).details;
}
